import { useCallback, useRef } from 'react'

/** Gap between the bottom chrome and anything floating above it. */
const GAP_PX = 12

/**
 * Let whatever occupies the bottom of a screen declare its own height, so
 * floating surfaces (the update banner, toasts) sit ABOVE it instead of on it.
 *
 * Both of those were hardcoded to `bottom-24`, an offset picked to clear the
 * bottom TAB BAR. Screens without a tab bar have a bottom ACTION BLOCK instead
 * — and the share door's is the join button. Measured 2026-08-09 at 390×844:
 * the update banner covered **42% of "Unirme al partido"**, on the one screen
 * whose entire job is getting that button pressed.
 *
 * Returns a CALLBACK REF, not a hook over an object ref, and that distinction
 * is the whole fix. The first version took a `useRef` object and measured it in
 * `useLayoutEffect`. On the share door that runs while the match query is still
 * pending, when the page renders a bare `aria-busy` shell and the action block
 * does not exist — so `ref.current` was null, the effect returned early, and
 * with a stable `[ref]` dependency it never ran again. It passed every local
 * check because `dev:preview` skips the pending phase and paints the block on
 * the first frame; against real Firebase the variable stayed at its default and
 * the banner sat on the button exactly as before. A callback ref is invoked BY
 * REACT when the node attaches and again with null when it detaches, so it
 * cannot be early.
 */
export function useFloatingOffset(): (node: HTMLElement | null) => void {
  const teardown = useRef<(() => void) | null>(null)

  return useCallback((node: HTMLElement | null) => {
    teardown.current?.()
    teardown.current = null

    const clear = () => document.documentElement.style.removeProperty('--floating-bottom')
    if (!node) {
      clear()
      return
    }

    const apply = () => {
      const h = node.getBoundingClientRect().height
      document.documentElement.style.setProperty('--floating-bottom', `${Math.round(h) + GAP_PX}px`)
    }
    apply()

    // The block grows and shrinks — a validation message appears, a button
    // swaps to its pending label — so a one-shot measurement goes stale.
    //
    // jsdom does NOT implement ResizeObserver, so constructing one
    // unconditionally would throw inside any component test that renders a
    // screen using this hook. The measurement above has already run; only the
    // keep-it-fresh part is skipped, which is right for a DOM with no layout
    // engine.
    if (typeof ResizeObserver === 'undefined') {
      teardown.current = clear
      return
    }
    const ro = new ResizeObserver(apply)
    ro.observe(node)
    teardown.current = () => {
      ro.disconnect()
      clear()
    }
  }, [])
}
